import { useState } from 'react';
import { useSession } from '@/auth/index';
import { useSriracha } from '@/notifications/index';
import { SerializedPerson } from '@/reports/index';
import { RelatedReportCard } from '@/reports/sections/RelatedReportsSection/RelatedReportCard';
import { RelatedReports } from '@/reports/sections/RelatedReportsSection/RelatedReports';
import { ReportSectionStyled } from '@/reports/shared/ReportSection/ReportSection';
import { commonTypes, serializedTypes } from '@/reports/types';
import { Box, Stack, Text } from '@/theme/index';

export interface RelatedReportsSectionProps {
  reportTitle?: string;
  reportType: string;
  reportOwner: string;
  potentialOwners: SerializedPerson[] | undefined;
  relatives: serializedTypes.Connection[] | undefined;
  addresses: commonTypes.Addresses;
  phones: commonTypes.Phones;
  emails: commonTypes.Emails;
  usernames: commonTypes.Usernames;
  message?: string;
  hasDefaultContactSearch?: boolean;
}

export function RelatedReportsSection({
  reportTitle,
  reportType,
  reportOwner,
  potentialOwners,
  relatives,
  addresses,
  phones,
  emails,
  usernames,
  message,
  hasDefaultContactSearch,
}: RelatedReportsSectionProps) {
  const [hasGeneratedNotifications, setHasGeneratedNotifications] =
    useState(false);
  const {
    session: { account },
  } = useSession();
  const userId = account?.account.user_info?.user_code;
  const { sendNotification, getSuggestedReportNotificationSources } =
    useSriracha();
  const { data } = getSuggestedReportNotificationSources;

  const relatedReports = new RelatedReports(
    reportType,
    reportTitle || reportOwner,
    potentialOwners,
    relatives,
    addresses,
    phones,
    emails,
    usernames
  );

  const generateSuggestedReportNotification = (notificationData: unknown) => {
    sendNotification.mutate(notificationData);
  };

  const relatedReportList = relatedReports.getRelatedReports();

  if (userId && data && !hasGeneratedNotifications) {
    const suggestedReportNotificationSources = data?.suggested_report_sources;
    if (
      relatedReports.shouldGenerateSuggestedReportNotifications(
        suggestedReportNotificationSources
      )
    ) {
      setHasGeneratedNotifications(true);
      relatedReports.generateNotificationsForSuggestedReports(
        generateSuggestedReportNotification,
        relatedReportList,
        userId
      );
    }
  }

  if (relatedReportList.length === 0) return null;

  return (
    <ReportSectionStyled>
      <Stack direction="column" spacing={2.5}>
        <Box>
          <Text variant="h3">Suggestions for you</Text>
          <Text variant="h2">
            Curious to find out even more about {reportTitle || reportOwner}?
          </Text>
          <Text>
            {message ? (
              <>{message}</>
            ) : (
              <>View potential owners of this {reportType} to learn more</>
            )}
          </Text>
        </Box>

        {relatedReportList.map((relatedReport, index) => {
          return (
            <RelatedReportCard
              relatedReport={relatedReport}
              key={index}
              hasDefaultContactSearch={hasDefaultContactSearch}
            />
          );
        })}
      </Stack>
    </ReportSectionStyled>
  );
}
